import { decodeLocalSession, getLocalSessionToken } from '../../api/authToken';

export const IMPERSONATION_ACTIVE_KEY = 'impersonation_active';
export const IMPERSONATION_TARGET_USER_KEY = 'impersonation_target_user_id';
export const IMPERSONATION_COMPANY_KEY = 'impersonation_company_id';
export const IMPERSONATION_ADMIN_UID_KEY = 'impersonation_admin_uid';
export const IMPERSONATION_TARGET_META_KEY = 'impersonation_target_meta';
export const IMPERSONATION_EXITING_KEY = 'impersonation_exiting';
export const IMPERSONATION_ENTERING_KEY = 'impersonation_entering';

// Only the page that started the return should show "Returning to admin panel".
// The stored flag survives logout because the admin panel is a different site
// and cannot clear this origin's storage.
let impersonationExitArmed = false;

function readStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key, value) {
  try {
    if (value == null || value === '') {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch {
    // Session token remains the source of truth if storage is unavailable.
  }
}

export function setImpersonationSession({ userId, companyId, adminUid, target } = {}) {
  writeStorage(IMPERSONATION_ACTIVE_KEY, 'true');
  writeStorage(IMPERSONATION_TARGET_USER_KEY, userId || null);
  writeStorage(IMPERSONATION_COMPANY_KEY, companyId || null);
  writeStorage(IMPERSONATION_ADMIN_UID_KEY, adminUid || null);
  if (target && (target.email || target.name || target.companyName)) {
    try {
      writeStorage(IMPERSONATION_TARGET_META_KEY, JSON.stringify({
        email: target.email || null,
        name: target.name || null,
        companyName: target.companyName || null,
      }));
    } catch {
      writeStorage(IMPERSONATION_TARGET_META_KEY, null);
    }
  }
}

export function clearImpersonationSession() {
  writeStorage(IMPERSONATION_ACTIVE_KEY, null);
  writeStorage(IMPERSONATION_TARGET_USER_KEY, null);
  writeStorage(IMPERSONATION_COMPANY_KEY, null);
  writeStorage(IMPERSONATION_ADMIN_UID_KEY, null);
  writeStorage(IMPERSONATION_TARGET_META_KEY, null);
  writeStorage(IMPERSONATION_ENTERING_KEY, null);
  writeStorage(IMPERSONATION_EXITING_KEY, null);
  impersonationExitArmed = false;
  try {
    // Legacy key from older impersonation flow
    localStorage.removeItem('impersonation_token');
  } catch {
    // ignore
  }
}

export function getSignedImpersonationSession() {
  const payload = decodeLocalSession(getLocalSessionToken());
  if (!payload?.impersonatorAdminId || !payload?.uid) return null;
  return payload;
}

export function isImpersonationSessionActive() {
  return Boolean(getSignedImpersonationSession()) || readStorage(IMPERSONATION_ACTIVE_KEY) === 'true';
}

export function getImpersonationTargetMeta() {
  const raw = readStorage(IMPERSONATION_TARGET_META_KEY);
  if (!raw) {
    return {
      userId: readStorage(IMPERSONATION_TARGET_USER_KEY),
      companyId: readStorage(IMPERSONATION_COMPANY_KEY),
      email: null,
      name: null,
      companyName: null,
    };
  }
  try {
    const parsed = JSON.parse(raw);
    return {
      userId: readStorage(IMPERSONATION_TARGET_USER_KEY),
      companyId: readStorage(IMPERSONATION_COMPANY_KEY),
      email: parsed?.email || null,
      name: parsed?.name || null,
      companyName: parsed?.companyName || null,
    };
  } catch {
    return {
      userId: readStorage(IMPERSONATION_TARGET_USER_KEY),
      companyId: readStorage(IMPERSONATION_COMPANY_KEY),
      email: null,
      name: null,
      companyName: null,
    };
  }
}

export function getImpersonationTargetLabel() {
  const meta = getImpersonationTargetMeta();
  return meta.name || meta.email || meta.companyName || null;
}

export function markImpersonationEntering() {
  // A previous "return to admin" flag lives on this origin and is never
  // cleared by admin logout. Drop it before signing into the company.
  impersonationExitArmed = false;
  writeStorage(IMPERSONATION_EXITING_KEY, null);
  writeStorage(IMPERSONATION_ENTERING_KEY, 'true');
}

export function isImpersonationEntering() {
  return readStorage(IMPERSONATION_ENTERING_KEY) === 'true';
}

export function clearImpersonationEntering() {
  writeStorage(IMPERSONATION_ENTERING_KEY, null);
}

export function markImpersonationExiting() {
  impersonationExitArmed = true;
  writeStorage(IMPERSONATION_EXITING_KEY, 'true');
}

export function isImpersonationExiting() {
  return impersonationExitArmed;
}

export function clearImpersonationExiting() {
  impersonationExitArmed = false;
  writeStorage(IMPERSONATION_EXITING_KEY, null);
}

/** Drop a previous visit's return-to-admin flag. A new page load is not mid-exit. */
export function clearStaleImpersonationExit() {
  if (impersonationExitArmed) return;
  writeStorage(IMPERSONATION_EXITING_KEY, null);
}

export function isImpersonationHandoffActive() {
  return isImpersonationEntering() || isImpersonationExiting() || isImpersonationSessionActive();
}
